import { createFeature, createReducer, on } from '@ngrx/store';
import { EntityMeta, Investor } from '@qtek/shared/models';
import { ParticipantActions } from './participants.actions';

export interface ParticipantState {
  investors: Investor[];
  dealBook: any[];
  invests: any[];
  invitations: any[];
  invitationsMeta: EntityMeta;
  dealBookMeta: EntityMeta;
  isInvestorsLoading: boolean;
}

const initialState: ParticipantState = {
  investors: [],
  dealBook: [],
  invests: [],
  invitations: [],
  invitationsMeta: new EntityMeta(),
  dealBookMeta: new EntityMeta(),
  isInvestorsLoading: false,
};

export const participantReducer = createReducer(
  initialState,
  on(ParticipantActions.getInvestorListSuccess, (state, { payload }) => ({
    ...state,
    investors: payload || [],
    isInvestorsLoading: false,
  })),
  on(ParticipantActions.getInvestorListFailure, state => ({
    ...state,
    isInvestorsLoading: false,
  })),
  on(ParticipantActions.addInvestorSuccess, (state, { payload }) => ({
    ...state,
    investors: state.investors.concat(payload),
  })),
  on(ParticipantActions.updateInvestorSuccess, (state, { payload }) => ({
    ...state,
    investors: state.investors.map(investor =>
      investor._id !== payload._id ? investor : { ...payload }
    ),
  })),
  on(ParticipantActions.deleteInvestorSuccess, (state, { payload }) => ({
    ...state,
    investors: state.investors.filter(investor => investor._id !== payload._id),
  })),
  on(ParticipantActions.subscribeInvestorList, state => ({
    ...state,
    isInvestorsLoading: true,
  })),
  on(
    ParticipantActions.unsubscribeInvestorList,
    (state): ParticipantState => ({
      ...state,
      investors: [],
    })
  ),
  on(
    ParticipantActions.getSalesPipelineListSuccess,
    (state, { res, meta }) => ({
      ...state,
      invitations: res ?? [],
      invitationsMeta: meta ?? new EntityMeta(),
    })
  ),
  on(ParticipantActions.createSalesPipelineSuccess, (state, { payload }) => ({
    ...state,
    invitations: [...state.invitations, ...(payload ? [payload] : [])],
  })),
  on(ParticipantActions.updateSalesPipelineSuccess, (state, { payload }) => ({
    ...state,
    invitations: state.invitations.map(deal =>
      deal?._id !== payload._id ? deal : payload
    ),
  })),
  on(ParticipantActions.deleteSalesPipelineSuccess, (state, { payload }) => ({
    ...state,
    invitations: state.invitations.filter(deal => deal?._id !== payload._id),
  })),
  on(
    ParticipantActions.unsubscribeSalesPipelineList,
    (state): ParticipantState => ({
      ...state,
      invitations: [],
    })
  ),
  on(
    ParticipantActions.getDealBookSuccess,
    ParticipantActions.getDealBookListSuccess,
    (state, { payload, meta }) => ({
      ...state,
      dealBook: payload ?? [],
      dealBookMeta: meta ?? new EntityMeta(),
    })
  ),
  on(ParticipantActions.createDealBookSuccess, (state, { payload }) => ({
    ...state,
    dealBook: [...state.dealBook, ...(payload ? [payload] : [])],
  })),
  on(
    ParticipantActions.updateDealBookSuccess,
    ParticipantActions.updateDealBookSuccess,
    (state, { payload }) => ({
      ...state,
      dealBook: state.dealBook.map(deal =>
        deal?._id !== payload._id ? deal : payload
      ),
    })
  ),
  on(
    ParticipantActions.deleteDealBookSuccess,
    ParticipantActions.deleteDealBookSuccess,
    (state, { payload }) => ({
      ...state,
      dealBook: state.dealBook.filter(deal => deal?._id !== payload._id),
    })
  ),
  on(
    ParticipantActions.unsubscribeDealBookList,
    (state): ParticipantState => ({
      ...state,
      dealBook: [],
    })
  ),
  on(ParticipantActions.getInvestsSuccess, (state, { payload }) => ({
    ...state,
    invests: payload.res,
  }))
);

export const ParticipantFeature = createFeature({
  name: 'participants',
  reducer: participantReducer,
});

export const {
  selectInvestors,
  selectIsInvestorsLoading,
  selectDealBook,
  selectDealBookMeta,
  selectInvests,
  selectInvitations,
  selectInvitationsMeta,
} = ParticipantFeature;
